import type { AppSection } from "../layouts/AppShell";
import {
  editableFieldPaths,
  isEditableFieldPath,
  normalizeFieldPath,
  resolveFieldAction,
  type EditableFieldPath,
} from "./fieldActionMap";
export {
  editableFieldPaths,
  isEditableFieldPath,
  type EditableFieldPath,
} from "./fieldActionMap";

export const positionTabs = [
  "overview",
  "transfer",
  "sales",
  "analytics",
  "journal",
  "quality",
] as const;
export type PositionTab = (typeof positionTabs)[number];
export interface ReturnTarget {
  section: AppSection;
  entityId?: string;
  tab?: PositionTab;
  filter?: string;
  scrollTarget?: string;
}
type PositionPayload = { positionId: string; returnTarget?: ReturnTarget };
export type AppAction =
  | { type: "open-position"; payload: PositionPayload }
  | { type: "edit-position"; payload: PositionPayload }
  | {
      type: "edit-position-field";
      payload: PositionPayload & {
        fieldPath: string;
        saleId?: string;
      };
    }
  | { type: "open-data-quality-issue" | "open-position-issue-detail"; payload: PositionPayload & { issueId:string; fieldPath:string; saleId?:string } }
  | {
      type: "open-position-tab";
      payload: PositionPayload & { tab: PositionTab; saleId?: string };
    }
  | { type: "add-sale"; payload: PositionPayload }
  | { type: "edit-sale"; payload: PositionPayload & { saleId: string } }
  | {
      type: "open-transfer" | "edit-transfer";
      payload: PositionPayload & { saleId?: string };
    }
  | {
      type: "open-listing" | "edit-listing" | "open-data-quality";
      payload: PositionPayload;
    }
  | {
      type: "open-event";
      payload: { eventId: string; returnTarget?: ReturnTarget };
    }
  | {
      type: "open-watchlist";
      payload: { watchlistId: string; returnTarget?: ReturnTarget };
    }
  | {
      type: "open-provider";
      payload: { providerId: string; returnTarget?: ReturnTarget };
    }
  | {
      type: "open-alert";
      payload: { alertId: string; returnTarget?: ReturnTarget };
    }
  | {
      type: "open-calendar-item";
      payload: { calendarItemId: string; returnTarget?: ReturnTarget };
    }
  | {
      type: "open-feed-item";
      payload: { feedItemId: string; returnTarget?: ReturnTarget };
    }
  | {
      type: "open-intelligence";
      payload: { entityId: string; returnTarget?: ReturnTarget };
    };
export type AppActionType = AppAction["type"];
export interface ActionRegistration {
  label: string;
  entityKey: string;
  fallback: AppSection;
}
export const actionRegistry: Readonly<
  Record<AppActionType, ActionRegistration>
> = {
  "open-position": {
    label: "Otevřít pozici",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "edit-position": {
    label: "Upravit pozici",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "edit-position-field": {
    label: "Upravit pole pozice",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "open-data-quality-issue": { label:"Otevřít detail problému", entityKey:"positionId", fallback:"portfolio" },
  "open-position-issue-detail": { label:"Otevřít detail problému", entityKey:"positionId", fallback:"portfolio" },
  "open-position-tab": {
    label: "Otevřít pracovní prostor pozice",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "add-sale": {
    label: "Přidat prodej",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "edit-sale": {
    label: "Upravit prodej",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "open-transfer": {
    label: "Otevřít transfer",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "edit-transfer": {
    label: "Upravit transfer",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "open-listing": {
    label: "Otevřít listing",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "edit-listing": {
    label: "Upravit listing",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "open-data-quality": {
    label: "Otevřít kvalitu dat",
    entityKey: "positionId",
    fallback: "portfolio",
  },
  "open-event": {
    label: "Otevřít event",
    entityKey: "eventId",
    fallback: "market",
  },
  "open-watchlist": {
    label: "Otevřít Watchlist",
    entityKey: "watchlistId",
    fallback: "watchlist",
  },
  "open-provider": {
    label: "Otevřít providera",
    entityKey: "providerId",
    fallback: "providers",
  },
  "open-alert": {
    label: "Otevřít upozornění",
    entityKey: "alertId",
    fallback: "alerts",
  },
  "open-calendar-item": {
    label: "Otevřít položku kalendáře",
    entityKey: "calendarItemId",
    fallback: "calendar",
  },
  "open-feed-item": {
    label: "Otevřít příležitost",
    entityKey: "feedItemId",
    fallback: "feed",
  },
  "open-intelligence": {
    label: "Otevřít intelligence",
    entityKey: "entityId",
    fallback: "intelligence",
  },
};
export interface ActionTarget {
  action: AppAction;
  version: 1;
}
export interface PositionOpeningState {
  positionId: string;
  mode: "view" | "edit";
  tab: PositionTab;
  fieldPath?: EditableFieldPath;
  section?: string;
  issueId?: string;
  fallbackWarning?: string;
  saleId?: string;
  focusTarget?: string;
  returnTarget?: ReturnTarget;
}
export type ActionDestination = {
  section: AppSection;
  entityId?: string;
  position?: PositionOpeningState;
  returnTarget?: ReturnTarget;
};

export const fieldPathRoute = (path: EditableFieldPath, saleId?: string) =>
  resolveFieldAction(path, saleId);
export interface OpenPositionEditorOptions { positionId:string; section?:string; fieldPath?:string; focusTarget?:string; issueId?:string; saleId?:string; returnContext?:ReturnTarget }
export const openPositionEditor=(options:OpenPositionEditorOptions):PositionOpeningState=>{
  const fieldPath=normalizeFieldPath(options.fieldPath);
  const route=fieldPath?resolveFieldAction(fieldPath,options.saleId):null;
  return {positionId:options.positionId,mode:route?.mode??'edit',tab:route?.tab??'overview',section:options.section??route?.section??'basic',...(fieldPath?{fieldPath}:{}),...(options.saleId?{saleId:options.saleId}:{}),...(options.focusTarget??route?.focusTarget?{focusTarget:options.focusTarget??route?.focusTarget}:{}),...(options.issueId?{issueId:options.issueId}:{}),...(options.returnContext?{returnTarget:options.returnContext}:{}),...(options.fieldPath&&!fieldPath?{fallbackWarning:'Konkrétní pole se nepodařilo otevřít. Pozici můžete upravit v celém formuláři.'}:{})};
};
export const buildActionTarget = (action: AppAction): ActionTarget => ({
  action: structuredClone(action),
  version: 1,
});
export function parseActionTarget(value: unknown): ActionTarget | null {
  if (!value || typeof value !== "object") return null;
  const target = value as Partial<ActionTarget>;
  if (
    target.version !== 1 ||
    !target.action ||
    typeof target.action !== "object" ||
    typeof target.action.type !== "string" ||
    !("payload" in target.action)
  )
    return null;
  const type = target.action.type as AppActionType,
    registration = actionRegistry[type];
  if (!registration) return null;
  const p = (target.action as AppAction).payload as Record<string, unknown>;
  if (
    !p ||
    typeof p !== "object" ||
    typeof p[registration.entityKey] !== "string" ||
    !(p[registration.entityKey] as string).trim()
  )
    return null;
  if ((type === "edit-position-field" || type === "open-data-quality-issue" || type === "open-position-issue-detail") && !normalizeFieldPath(p.fieldPath)) return null;
  if (
    type === "open-position-tab" &&
    !positionTabs.includes(p.tab as PositionTab)
  )
    return null;
  if (
    type === "edit-sale" &&
    (typeof p.saleId !== "string" || !p.saleId.trim())
  )
    return null;
  if (p.returnTarget !== undefined && !isReturnTarget(p.returnTarget))
    return null;
  return structuredClone(target as ActionTarget);
}
function isReturnTarget(value: unknown): value is ReturnTarget {
  if (!value || typeof value !== "object") return false;
  const target = value as ReturnTarget;
  return (
    typeof target.section === "string" &&
    [
      "dashboard",
      "feed",
      "alerts",
      "market",
      "portfolio",
      "watchlist",
      "calendar",
      "profit",
      "intelligence",
      "providers",
      "accounts",
    ].includes(target.section) &&
    (!target.tab || positionTabs.includes(target.tab))
  );
}
export function resolveAction(action: AppAction): ActionDestination {
  const p = action.payload as any;
  switch (action.type) {
    case "open-position":
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: {
          positionId: p.positionId,
          mode: "view",
          tab: "overview",
          returnTarget: p.returnTarget,
        },
      };
    case "edit-position":
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: openPositionEditor({positionId:p.positionId,returnContext:p.returnTarget}),
      };
    case "edit-position-field": {
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: openPositionEditor({positionId:p.positionId,fieldPath:p.fieldPath,saleId:p.saleId,returnContext:p.returnTarget}),
      };
    }
    case "open-data-quality-issue":
    case "open-position-issue-detail": return {section:"portfolio",entityId:p.positionId,position:openPositionEditor({positionId:p.positionId,fieldPath:p.fieldPath,issueId:p.issueId,saleId:p.saleId,returnContext:p.returnTarget})};
    case "open-position-tab":
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: {
          positionId: p.positionId,
          mode: "view",
          tab: p.tab,
          saleId: p.saleId,
          returnTarget: p.returnTarget,
        },
      };
    case "add-sale":
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: {
          positionId: p.positionId,
          mode: "view",
          tab: "sales",
          focusTarget: "sale-new-sale-date",
          returnTarget: p.returnTarget,
        },
      };
    case "edit-sale":
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: {
          positionId: p.positionId,
          mode: "view",
          tab: "sales",
          saleId: p.saleId,
          focusTarget: `sale-${p.saleId}-sale-date`,
          returnTarget: p.returnTarget,
        },
      };
    case "open-transfer":
    case "edit-transfer":
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: {
          positionId: p.positionId,
          mode: "view",
          tab: "transfer",
          saleId: p.saleId,
          focusTarget:
            action.type === "edit-transfer" && p.saleId
              ? `sale-${p.saleId}-transfer-status`
              : undefined,
          returnTarget: p.returnTarget,
        },
      };
    case "open-listing":
    case "edit-listing":
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: {
          positionId: p.positionId,
          mode: "view",
          tab: "sales",
          focusTarget: action.type === "edit-listing" ? "listing" : undefined,
          returnTarget: p.returnTarget,
        },
      };
    case "open-data-quality":
      return {
        section: "portfolio",
        entityId: p.positionId,
        position: {
          positionId: p.positionId,
          mode: "view",
          tab: "quality",
          returnTarget: p.returnTarget,
        },
      };
    case "open-event":
      return {
        section: "market",
        entityId: p.eventId,
        returnTarget: p.returnTarget,
      };
    case "open-watchlist":
      return {
        section: "watchlist",
        entityId: p.watchlistId,
        returnTarget: p.returnTarget,
      };
    case "open-provider":
      return {
        section: "providers",
        entityId: p.providerId,
        returnTarget: p.returnTarget,
      };
    case "open-alert":
      return {
        section: "alerts",
        entityId: p.alertId,
        returnTarget: p.returnTarget,
      };
    case "open-calendar-item":
      return {
        section: "calendar",
        entityId: p.calendarItemId,
        returnTarget: p.returnTarget,
      };
    case "open-feed-item":
      return {
        section: "feed",
        entityId: p.feedItemId,
        returnTarget: p.returnTarget,
      };
    case "open-intelligence":
      return {
        section: "intelligence",
        entityId: p.entityId,
        returnTarget: p.returnTarget,
      };
  }
}
export function legacyDeepLinkToAction(
  link: { section: string; entityId?: string; tab?: string },
  saleId?: string,
): AppAction | null {
  if (!link.entityId) return null;
  if (link.section === "portfolio") {
    const tab =
      link.tab === "listing"
        ? "sales"
        : link.tab === "detail"
          ? "overview"
          : link.tab;
    if (tab && positionTabs.includes(tab as PositionTab))
      return {
        type: "open-position-tab",
        payload: { positionId: link.entityId, tab: tab as PositionTab, saleId },
      };
    return { type: "open-position", payload: { positionId: link.entityId } };
  }
  if (link.section === "watchlist")
    return { type: "open-watchlist", payload: { watchlistId: link.entityId } };
  if (link.section === "providers")
    return { type: "open-provider", payload: { providerId: link.entityId } };
  if (link.section === "alerts")
    return { type: "open-alert", payload: { alertId: link.entityId } };
  if (link.section === "calendar")
    return {
      type: "open-calendar-item",
      payload: { calendarItemId: link.entityId },
    };
  if (link.section === "market")
    return { type: "open-event", payload: { eventId: link.entityId } };
  if (link.section === "feed")
    return { type: "open-feed-item", payload: { feedItemId: link.entityId } };
  if (link.section === "intelligence")
    return { type: "open-intelligence", payload: { entityId: link.entityId } };
  return null;
}
